import { useEffect, useState } from 'react';
import {
  ArrowPathIcon, ArrowTopRightOnSquareIcon, CheckCircleIcon, ClipboardDocumentIcon, CloudIcon, ExclamationTriangleIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import {
  anbindungEntfernen, anbindungenListe, anbindungPruefen, anbindungSpeichern, type AnbindungApp, type AnbindungPruefung,
} from '../../api/client';
import { useDialog } from '../../contexts/ConfirmContext';

/**
 * Betreiber-Konsole → Cloud-Anbindungen (E34): Google, Microsoft und Dropbox lassen OAuth nur mit einer registrierten
 * App zu. Der Betreiber trägt sie hier einmal ein — danach verbinden Firmen-Admins ihre Ablage unter Laufwerk → Quellen
 * nur noch mit der Anmeldung beim Anbieter. Das Secret kommt nie zurück an die Oberfläche.
 */
export default function CloudAnbindungen({ flash }: { flash: (msg: string, variant?: 'ok' | 'err') => void }) {
  const [daten, setDaten] = useState<{ rueckruf: string; apps: AnbindungApp[] } | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = async () => {
    try { setDaten(await anbindungenListe()); setFehler(null); }
    catch (e: any) { setFehler(e?.message ?? String(e)); }
  };
  useEffect(() => { void laden(); }, []);

  return (
    <section className="space-y-4">
      <div className="card">
        <h2 className="page-h1 mb-1 flex items-center gap-2"><CloudIcon className="h-5 w-5" /> Cloud-Anbindungen</h2>
        <p className="max-w-3xl text-xs leading-relaxed text-ps-dark-500">
          Google, Microsoft und Dropbox lassen eine Anmeldung nur über eine dort registrierte App zu. Diese
          powerCOMPANY-App registrieren Sie hier <b>einmal für alle Firmen</b>. Danach verbinden Firmen-Admins ihre Ablage
          unter Laufwerk → Quellen selbst: Anmeldung beim Anbieter, Lesezugriff erlauben, fertig. powerCOMPANY verlangt
          dabei nur Leserechte.
        </p>
        {daten && <Rueckruf url={daten.rueckruf} />}
      </div>
      {fehler && <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{fehler}</div>}
      {!daten && !fehler && <div className="card text-sm text-ps-dark-400">Lädt …</div>}
      {daten?.apps.map((a) => (
        <AppKarte key={a.anbieter} app={a} rueckruf={daten.rueckruf} flash={flash}
          onNeu={(neu) => setDaten((d) => d && { ...d, apps: d.apps.map((x) => (x.anbieter === neu.anbieter ? neu : x)) })} />
      ))}
    </section>
  );
}

function Rueckruf({ url }: { url: string }) {
  return (
    <div className="mt-3">
      <div className="mb-1 text-[11px] font-medium text-ps-dark-600">Weiterleitungs-Adresse (bei allen drei Anbietern eintragen)</div>
      <div className="flex items-center gap-2">
        <code className="min-w-0 flex-1 break-all rounded bg-gray-100 px-2 py-1.5 text-[12px] text-ps-dark-800">{url}</code>
        <button type="button" className="btn-ghost shrink-0 px-2 py-1.5 text-xs" aria-label="Adresse kopieren"
          onClick={() => void navigator.clipboard?.writeText(url)}><ClipboardDocumentIcon className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

function Status({ app }: { app: AnbindungApp }) {
  if (app.herkunft === 'konsole') return <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">Freigeschaltet</span>;
  if (app.herkunft === 'env') return <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">Freigeschaltet (Server-Datei .env)</span>;
  return <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">Noch nicht freigeschaltet</span>;
}

function AppKarte({ app, rueckruf, flash, onNeu }: {
  app: AnbindungApp; rueckruf: string; flash: (msg: string, variant?: 'ok' | 'err') => void; onNeu: (a: AnbindungApp) => void;
}) {
  const { confirm } = useDialog();
  const [clientId, setClientId] = useState(app.client_id ?? '');
  const [secret, setSecret] = useState('');
  const [anleitung, setAnleitung] = useState(!app.herkunft);
  const [busy, setBusy] = useState<'pruefen' | 'speichern' | 'entfernen' | null>(null);
  const [pruefung, setPruefung] = useState<AnbindungPruefung | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const hinterlegt = !!app.herkunft;
  const idGleich = hinterlegt && clientId.trim() === (app.client_id ?? '');

  const pruefen = async () => {
    setBusy('pruefen'); setPruefung(null); setFehler(null);
    try { setPruefung(await anbindungPruefen(app.anbieter, { client_id: clientId.trim(), client_secret: secret || undefined })); }
    catch (e: any) { setFehler(e?.message ?? String(e)); }
    finally { setBusy(null); }
  };

  const speichern = async (e: React.FormEvent) => {
    e.preventDefault();
    if (hinterlegt && !idGleich && !await confirm({
      title: `${app.titel}: andere App verwenden?`,
      message: `Bestehende Verbindungen zu ${app.fuer.join(', ')} gehören zur bisherigen App. Sie müssen sich danach unter Laufwerk → Quellen neu anmelden.`,
      okLabel: 'App wechseln',
    })) return;
    setBusy('speichern'); setPruefung(null); setFehler(null);
    try {
      const r = await anbindungSpeichern(app.anbieter, { client_id: clientId.trim(), client_secret: secret || undefined });
      setSecret(''); setPruefung(r.pruefung); onNeu(r); setAnleitung(false);
      flash(`${app.titel} freigeschaltet — Firmen können sich jetzt anmelden`);
    } catch (ex: any) { setFehler(ex?.message ?? String(ex)); }
    finally { setBusy(null); }
  };

  const entfernen = async () => {
    if (!await confirm({
      title: `${app.titel}-App entfernen?`,
      message: `Bestehende Verbindungen zu ${app.fuer.join(', ')} können danach nicht mehr lesen, bis wieder eine App hinterlegt ist.`,
      okLabel: 'Entfernen', danger: true,
    })) return;
    setBusy('entfernen'); setPruefung(null); setFehler(null);
    try {
      const r = await anbindungEntfernen(app.anbieter);
      onNeu(r); setClientId(r.client_id ?? ''); setSecret('');
      flash(r.herkunft ? `${app.titel}: Eintrag entfernt — es gilt wieder die App aus der .env` : `${app.titel}-App entfernt`);
    } catch (ex: any) { flash(ex?.message ?? String(ex), 'err'); }
    finally { setBusy(null); }
  };

  return (
    <div className="card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <h3 className="flex flex-wrap items-center gap-2 text-base font-semibold text-ps-dark-900">{app.titel} <Status app={app} /></h3>
          <p className="mt-0.5 text-xs text-ps-dark-500">Für {app.fuer.join(' und ')}
            {app.geaendert && <> · hinterlegt am {new Date(app.geaendert).toLocaleDateString('de-CH')}{app.geaendert_von ? ` von ${app.geaendert_von}` : ''}</>}
          </p>
        </div>
        <button type="button" onClick={() => setAnleitung((x) => !x)} className="text-xs font-medium text-primary-700 hover:underline">
          {anleitung ? 'Anleitung ausblenden' : 'Anleitung zeigen'}
        </button>
      </div>

      {anleitung && (
        <ol className="mt-3 space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs leading-relaxed text-ps-dark-700">
          {app.schritte.map((s, i) => (
            <li key={i} className="flex gap-2">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-100 text-[11px] font-semibold text-primary-800">{i + 1}</span>
              <span className="min-w-0">
                {s.text}
                {s.link && (
                  <a href={s.link} target="_blank" rel="noreferrer" className="ml-1 inline-flex items-center gap-0.5 whitespace-nowrap font-medium text-primary-700 hover:underline">
                    öffnen <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                  </a>
                )}
              </span>
            </li>
          ))}
          <li className="pt-1"><Rueckruf url={rueckruf} /></li>
        </ol>
      )}

      <form onSubmit={speichern} className="mt-3 space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor={`${app.anbieter}-id`} className="mb-1 block text-xs font-medium text-ps-dark-600">{app.feld_id} <span className="text-rose-600">*</span></label>
            <input id={`${app.anbieter}-id`} value={clientId} onChange={(e) => setClientId(e.target.value)} required maxLength={300}
              autoComplete="off" spellCheck={false} className="input-field font-mono text-xs" />
          </div>
          <div>
            <label htmlFor={`${app.anbieter}-secret`} className="mb-1 block text-xs font-medium text-ps-dark-600">
              {app.feld_secret} {!idGleich && <span className="text-rose-600">*</span>}
            </label>
            <input id={`${app.anbieter}-secret`} type="password" value={secret} onChange={(e) => setSecret(e.target.value)} maxLength={500}
              required={!idGleich} autoComplete="new-password" spellCheck={false}
              placeholder={idGleich ? 'hinterlegt — leer lassen, um es zu behalten' : ''} className="input-field font-mono text-xs" />
          </div>
        </div>

        {pruefung && (
          <div className={`flex items-start gap-1.5 rounded-lg border p-3 text-xs ${pruefung.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : pruefung.ok === false ? 'border-rose-200 bg-rose-50 text-rose-800' : 'border-amber-200 bg-amber-50 text-amber-900'}`}>
            {pruefung.ok ? <CheckCircleIcon className="h-4 w-4 shrink-0" /> : <ExclamationTriangleIcon className="h-4 w-4 shrink-0" />} {pruefung.meldung}
          </div>
        )}
        {fehler && <div className="flex items-start gap-1.5 rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800"><ExclamationTriangleIcon className="h-4 w-4 shrink-0" /> {fehler}</div>}

        <div className="flex flex-wrap items-center justify-end gap-2">
          {app.herkunft === 'konsole' && (
            <button type="button" onClick={() => void entfernen()} disabled={!!busy} className="btn-ghost mr-auto flex items-center gap-1.5 text-sm text-rose-700">
              <TrashIcon className="h-4 w-4" /> Entfernen
            </button>
          )}
          <button type="button" onClick={() => void pruefen()} disabled={!!busy || !clientId.trim() || (!secret && !idGleich)} className="btn-ghost flex items-center gap-1.5 text-sm">
            <ArrowPathIcon className={`h-4 w-4 ${busy === 'pruefen' ? 'animate-spin' : ''}`} /> Prüfen
          </button>
          <button type="submit" disabled={!!busy || !clientId.trim() || (idGleich && !secret)} className="btn-primary text-sm">
            {busy === 'speichern' ? 'Prüft und speichert …' : hinterlegt ? 'Ersetzen' : 'Speichern und freischalten'}
          </button>
        </div>
      </form>
    </div>
  );
}
